import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Avatar, roleLabels } from '../components/Avatar'
import { EventResponseInline } from '../components/EventResponseInline'
import { CoverResponseInline } from '../components/CoverResponseInline'
import { useApp } from '../store/AppContext'
import { threadIdFor } from '../data/seed'

export function ChatThread() {
  const { partnerId } = useParams<{ partnerId: string }>()
  const { currentUser, allUsers, messages, sendMessage, markThreadRead } = useApp()
  const navigate = useNavigate()
  const [text, setText] = useState('')

  const partner = allUsers.find((u) => u.id === partnerId)

  const thread = useMemo(() => {
    if (!partnerId) return []
    const threadId = threadIdFor(currentUser.id, partnerId)
    return messages.filter((m) => m.threadId === threadId).sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  }, [messages, currentUser, partnerId])

  useEffect(() => {
    if (partnerId) markThreadRead(partnerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, thread.length])

  if (!partner) {
    return (
      <div className="p-4">
        <p className="text-sm text-slate-500">Person not found.</p>
      </div>
    )
  }

  const submit = () => {
    if (!text.trim() || !partnerId) return
    sendMessage(partnerId, text)
    setText('')
  }

  return (
    <div className="flex h-screen flex-col">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+12px)] backdrop-blur">
        <button
          onClick={() => navigate('/chats')}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500"
        >
          ‹
        </button>
        <Avatar user={partner} size={36} />
        <div>
          <p className="text-sm font-bold text-slate-900">{partner.name}</p>
          <p className="text-xs text-slate-500">{partner.title ?? roleLabels[partner.role]}</p>
        </div>
      </header>

      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4 pb-28">
        {thread.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">
            Say hello to {partner.name.split(' ')[0]}.
          </div>
        )}
        {thread.map((m) => {
          const mine = m.senderId === currentUser.id
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm shadow-card ${
                  mine ? 'rounded-br-md bg-brand-600 text-white' : 'rounded-bl-md bg-white text-slate-800'
                }`}
              >
                <p>{m.text}</p>
                {m.eventId && !mine && <EventResponseInline eventId={m.eventId} />}
                {/* "Can you cover this shift?" — answered in the thread it arrived in. */}
                {m.coverRequestId && !mine && <CoverResponseInline requestId={m.coverRequestId} />}
                <p className={`mt-1 text-[10px] ${mine ? 'text-brand-100' : 'text-slate-400'}`}>
                  {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="Message..."
            className="input flex-1"
          />
          <button
            onClick={submit}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white"
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  )
}

